import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import AddNew from '@hellodarwin/icons/dist/icons/AddNew';
import Arrow from '@hellodarwin/icons/dist/icons/Arrow';
import Close from '@hellodarwin/icons/dist/icons/Close';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { ComposerCard, ComposerField, ComposerFooter, ComposerTextArea, LoadMoreWrapper, MentionButton, MessagesList, OverlayText, PanelContainer, ReplyComposerCancelButton, ReplyComposerExcerpt, ReplyComposerPreview, MessageReplyPreviewHeader, ReplyPreviewExcerptRow, ReplyPreviewOverflowIndicator, SendButton, SuggestionItem, SuggestionList, } from './ConversationPanel.styles';
import { MENTION_TRIGGER_REGEX, buildCanonicalContent, buildMentionSuggestions, formatMentionCandidatesInline, hasValidMention, mentionSuggestionsLimit, pruneMentionInstances, renderCanonicalMentions, } from './mention-utils';
import MessageItem from './MessageItem';
import { ConversationPanelContent } from './styles';
const emptyMentionCandidates = [];
const ConversationPanel = ({ messages, mentionCandidates, mentionExpansionOptions = [], initialMentionCandidates = emptyMentionCandidates, onSend, initialText, hasMore, loadingMore, onLoadMore, selfRole, onDeleteMessage, requireMention = true, selfId, embedded = false, labels, maxMessageLength = 10000, variant = 'conversation', }) => {
    var _a, _b, _c;
    const { t } = useTranslations();
    const theme = useTheme();
    const mentionPurple = theme.colors.purple_2;
    const mentionOrange = theme.colors.orange_2;
    const isCommentThread = variant === 'comment-thread';
    const mentionRequired = isCommentThread ? false : requireMention;
    const [showValidationHint, setShowValidationHint] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [text, setText] = useState(initialText !== null && initialText !== void 0 ? initialText : '');
    const previousInitialText = useRef(initialText !== null && initialText !== void 0 ? initialText : '');
    const [mentionSuggestionsOpen, setMentionSuggestionsOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [active, setActive] = useState(0);
    const textareaRef = useRef(null);
    const composerRef = useRef(null);
    const messageRefs = useRef(new Map());
    const [mentionInstances, setMentionInstances] = useState(initialMentionCandidates);
    const [replyParentMessage, setReplyParentMessage] = useState(null);
    const renderReplyingToLabel = (authorName) => {
        return (_jsxs(_Fragment, { children: [t('application|conversationReplyingTo'), " ", _jsx("strong", { children: authorName })] }));
    };
    const completeMentionCandidates = useMemo(() => mentionCandidates, [mentionCandidates]);
    const hasMentionOptions = useMemo(() => completeMentionCandidates.length > 0 ||
        mentionExpansionOptions.some((option) => option.candidates.length > 0), [completeMentionCandidates, mentionExpansionOptions]);
    useEffect(() => {
        const nextInitialText = initialText !== null && initialText !== void 0 ? initialText : '';
        if (text === previousInitialText.current) {
            setText(nextInitialText);
            setMentionInstances(initialMentionCandidates);
        }
        previousInitialText.current = nextInitialText;
    }, [initialText, initialMentionCandidates, text]);
    useEffect(() => {
        if (replyParentMessage &&
            !messages.some((message) => message.message_id === replyParentMessage.message_id)) {
            setReplyParentMessage(null);
        }
    }, [messages, replyParentMessage]);
    const loadedMessageIds = useMemo(() => new Set(messages.map((message) => message.message_id)), [messages]);
    const mentionSuggestions = useMemo(() => {
        const q = (mentionQuery !== null && mentionQuery !== void 0 ? mentionQuery : '').trim().toLowerCase();
        const expansionSuggestions = mentionExpansionOptions
            .filter((option) => option.candidates.length > 0)
            .filter((option) => !q || option.label.toLowerCase().includes(q))
            .map((option) => ({
            kind: 'expansion',
            option,
        }));
        const candidateSuggestions = buildMentionSuggestions(completeMentionCandidates, mentionQuery, mentionSuggestionsLimit).map((candidate) => ({
            kind: 'candidate',
            candidate,
        }));
        return [...expansionSuggestions, ...candidateSuggestions].slice(0, mentionSuggestionsLimit);
    }, [completeMentionCandidates, mentionExpansionOptions, mentionQuery]);
    const escapeHtml = (s) => s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const getCaret = () => {
        var _a, _b;
        const el = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea;
        return el ? el.selectionStart : 0;
    };
    const applyMentionInsert = (c, startIndex, endIndex, addSpaceBefore) => {
        var _a, _b;
        const el = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea;
        if (!el)
            return;
        const display = `@${c.label}`;
        const beforeRaw = text.slice(0, startIndex);
        const after = text.slice(endIndex);
        const spacer = addSpaceBefore && beforeRaw.length > 0 && !/\s$/.test(beforeRaw)
            ? ' '
            : '';
        const before = beforeRaw + spacer;
        const next = `${before}${display} ${after}`;
        const capped = next.slice(0, maxMessageLength);
        setText(capped);
        setMentionInstances((prev) => [...prev, c]);
        setMentionSuggestionsOpen(false);
        setMentionQuery('');
        setActive(0);
        const pos = Math.min(before.length + display.length + 1, maxMessageLength);
        window.requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(pos, pos);
        });
    };
    const applyExpansionInsert = (option, startIndex, endIndex, addSpaceBefore) => {
        var _a, _b;
        const el = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea;
        if (!el || option.candidates.length === 0)
            return;
        const display = formatMentionCandidatesInline(option.candidates, t('application|conversationMentionListConjunction'));
        const beforeRaw = text.slice(0, startIndex);
        const after = text.slice(endIndex);
        const spacer = addSpaceBefore && beforeRaw.length > 0 && !/\s$/.test(beforeRaw)
            ? ' '
            : '';
        const before = beforeRaw + spacer;
        const next = `${before}${display} ${after}`;
        const capped = next.slice(0, maxMessageLength);
        setText(capped);
        setMentionInstances((prev) => [...prev, ...option.candidates]);
        setMentionSuggestionsOpen(false);
        setMentionQuery('');
        setActive(0);
        const pos = Math.min(before.length + display.length + 1, maxMessageLength);
        window.requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(pos, pos);
        });
    };
    const insertMentionResolved = (suggestion) => {
        const caret = getCaret();
        const trig = detectTrigger(text, caret);
        const start = trig ? trig.start : caret;
        const addSpaceBefore = !trig;
        if (suggestion.kind === 'expansion') {
            applyExpansionInsert(suggestion.option, start, caret, addSpaceBefore);
            return;
        }
        applyMentionInsert(suggestion.candidate, start, caret, addSpaceBefore);
    };
    const detectTrigger = (text, caret) => {
        var _a, _b;
        const left = text.slice(0, caret);
        const mentionTriggerMatch = MENTION_TRIGGER_REGEX.exec(left);
        if (!mentionTriggerMatch)
            return null;
        const start = ((_a = mentionTriggerMatch.index) !== null && _a !== void 0 ? _a : 0) + mentionTriggerMatch[1].length;
        const query = (_b = mentionTriggerMatch[2]) !== null && _b !== void 0 ? _b : '';
        return { start, query };
    };
    const onChange = (e) => {
        const v = e.target.value;
        const capped = v.slice(0, maxMessageLength);
        setText(capped);
        const caret = getCaret();
        const trig = detectTrigger(capped, caret);
        if (trig) {
            setMentionSuggestionsOpen(true);
            setMentionQuery(trig.query);
            setActive(0);
        }
        else {
            setMentionSuggestionsOpen(false);
            setMentionQuery('');
        }
        if (mentionInstances.length > 0) {
            const nextInstances = pruneMentionInstances(capped, mentionInstances);
            if (nextInstances.length !== mentionInstances.length) {
                setMentionInstances(nextInstances);
            }
        }
    };
    const onFocus = () => {
        window.requestAnimationFrame(() => {
            const trig = detectTrigger(text, getCaret());
            if (trig) {
                setMentionSuggestionsOpen(true);
                setMentionQuery(trig.query);
                setActive(0);
                return;
            }
        });
    };
    const onBlur = () => {
        setShowValidationHint(true);
        window.setTimeout(() => {
            setMentionSuggestionsOpen(false);
            setMentionQuery('');
            setActive(0);
        }, 0);
    };
    const onKeyDown = (e) => {
        var _a;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (mentionSuggestionsOpen && mentionSuggestions.length > 0) {
                const currentSuggestion = (_a = mentionSuggestions[active]) !== null && _a !== void 0 ? _a : mentionSuggestions[0];
                if (currentSuggestion)
                    insertMentionResolved(currentSuggestion);
                return;
            }
            if (isValidToSend) {
                void handleSend();
                return;
            }
            setShowValidationHint(true);
            return;
        }
        if (mentionSuggestionsOpen) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((p) => Math.min(p + 1, Math.max(0, mentionSuggestions.length - 1)));
                return;
            }
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((p) => Math.max(p - 1, 0));
                return;
            }
            else if (e.key === 'Escape') {
                setMentionSuggestionsOpen(false);
                return;
            }
        }
    };
    const getSuggestionKey = (suggestion) => {
        var _a;
        if (suggestion.kind === 'expansion') {
            return `expansion:${suggestion.option.label}`;
        }
        const c = suggestion.candidate;
        return `${c.targetType}:${(_a = c.targetId) !== null && _a !== void 0 ? _a : ''}:${c.label}`;
    };
    const getSuggestionLabel = (suggestion) => suggestion.kind === 'expansion'
        ? suggestion.option.label
        : `@${suggestion.candidate.label}`;
    const hasMessageText = useMemo(() => (text !== null && text !== void 0 ? text : '').trim().length > 0, [text]);
    const isValidToSend = useMemo(() => {
        const trimmed = (text !== null && text !== void 0 ? text : '').trim();
        if (!trimmed)
            return false;
        return mentionRequired ? hasValidMention(text, mentionInstances) : true;
    }, [mentionInstances, mentionRequired, text]);
    const handleSend = async () => {
        if (!isValidToSend || isSending) {
            setShowValidationHint(true);
            return;
        }
        const trimmed = text.trim();
        const out = buildCanonicalContent(trimmed, mentionInstances, completeMentionCandidates);
        setIsSending(true);
        try {
            await onSend(out, replyParentMessage === null || replyParentMessage === void 0 ? void 0 : replyParentMessage.message_id);
            setText(initialText);
            setReplyParentMessage(null);
            setMentionSuggestionsOpen(false);
            setMentionQuery('');
            setActive(0);
            setMentionInstances(initialMentionCandidates);
        }
        catch (_a) {
        }
        finally {
            setIsSending(false);
        }
    };
    const handleReply = (message) => {
        setReplyParentMessage(message);
        window.requestAnimationFrame(() => {
            var _a, _b, _c;
            (_a = composerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            const el = (_c = (_b = textareaRef.current) === null || _b === void 0 ? void 0 : _b.resizableTextArea) === null || _c === void 0 ? void 0 : _c.textArea;
            el === null || el === void 0 ? void 0 : el.focus({ preventScroll: true });
        });
    };
    const handleJumpToMessage = (messageId) => {
        const el = messageRefs.current.get(messageId);
        if (!el) {
            return;
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const buildHighlightedHtml = () => {
        const ranges = [];
        const used = new Set();
        const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const addByBoundary = (token, color) => {
            const re = new RegExp(`(^|\\s)${escapeRegExp(token)}(?![\\w-])`, 'gi');
            let m;
            while ((m = re.exec(text)) !== null) {
                const start = m.index + (m[1] ? m[1].length : 0);
                if (!used.has(start)) {
                    ranges.push({ start, end: start + token.length, color });
                    used.add(start);
                }
            }
        };
        const tokenMap = new Map();
        for (const c of completeMentionCandidates) {
            const token = `@${c.label}`;
            const color = c.targetType === 'admin' || c.targetType === 'helloDarwin'
                ? mentionPurple
                : mentionOrange;
            if (!tokenMap.has(token))
                tokenMap.set(token, color);
        }
        for (const inst of mentionInstances) {
            const token = `@${inst.label}`;
            const color = inst.targetType === 'admin' || inst.targetType === 'helloDarwin'
                ? mentionPurple
                : mentionOrange;
            tokenMap.set(token, color);
        }
        tokenMap.forEach((color, token) => {
            addByBoundary(token, color);
        });
        ranges.sort((a, b) => a.start - b.start);
        let html = '';
        let i = 0;
        for (const r of ranges) {
            if (r.start > i)
                html += escapeHtml(text.slice(i, r.start));
            html += `<span style="color:${r.color};">${escapeHtml(text.slice(r.start, r.end))}</span>`;
            i = r.end;
        }
        if (i < text.length)
            html += escapeHtml(text.slice(i));
        return html.replace(/\n/g, '<br/>');
    };
    return (_jsxs(PanelContainer, { "$bare": embedded, children: [!embedded && (_jsxs("div", { children: [_jsx(Typography, { elementTheme: "subtitle1", children: t('application|conversationTitle') }), _jsx(Typography, { elementTheme: "body2", color: theme.colors.grey_2, children: t('application|conversationDescription') })] })), _jsx(ComposerCard, { ref: composerRef, "$bare": isCommentThread, children: _jsxs(ConversationPanelContent, { children: [(labels === null || labels === void 0 ? void 0 : labels.composerTitle) === false ? (_jsx("span", { "data-conversation-composer-title": true, hidden: true })) : (_jsx(Typography, { elementTheme: "subtitle2", children: (_a = labels === null || labels === void 0 ? void 0 : labels.composerTitle) !== null && _a !== void 0 ? _a : t('application|conversationTextBoxTitle') })), !!replyParentMessage && (_jsxs(ReplyComposerPreview, { "$clickable": true, role: "button", tabIndex: 0, onClick: () => handleJumpToMessage(replyParentMessage.message_id), onKeyDown: (e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') {
                                    return;
                                }
                                e.preventDefault();
                                handleJumpToMessage(replyParentMessage.message_id);
                            }, children: [_jsx(MessageReplyPreviewHeader, { children: _jsx(Typography, { elementTheme: "body3", color: theme.colors.grey_2, children: renderReplyingToLabel(replyParentMessage.author_name) }) }), _jsx(ReplyComposerCancelButton, { variant: "text", size: "extra-small", headingIcon: _jsx(Close, { size: 12 }), onClick: (e) => {
                                        e.stopPropagation();
                                        setReplyParentMessage(null);
                                    } }), _jsx(ReplyPreviewExcerptRow, { children: _jsx(ReplyComposerExcerpt, { children: _jsx(Typography, { elementTheme: "body2", style: {
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                            }, children: renderCanonicalMentions(replyParentMessage.content, mentionPurple, mentionOrange) }) }) }), _jsx(ReplyPreviewOverflowIndicator, { children: _jsx(Arrow, { direction: "down", size: 12 }) })] })), _jsxs(ComposerField, { children: [!isCommentThread && (_jsx(OverlayText, { "aria-hidden": true, dangerouslySetInnerHTML: {
                                        __html: buildHighlightedHtml() || '&nbsp;',
                                    } })), _jsx(ComposerTextArea, { ref: textareaRef, placeholder: (_b = labels === null || labels === void 0 ? void 0 : labels.placeholder) !== null && _b !== void 0 ? _b : t('application|conversationTextBoxPlaceholder'), autoSize: { minRows: 3 }, maxLength: maxMessageLength, value: text, disabled: isSending, onChange: onChange, onFocus: onFocus, onBlur: onBlur, onKeyDown: onKeyDown, style: {
                                        color: isCommentThread
                                            ? theme.colors.grey_1
                                            : theme.colors.transparent,
                                        caretColor: theme.colors.grey_1,
                                        position: 'relative',
                                        zIndex: 1,
                                    } }), mentionSuggestionsOpen && mentionSuggestions.length > 0 && (_jsx(SuggestionList, { children: mentionSuggestions.map((suggestion, i) => (_jsx(SuggestionItem, { onMouseDown: (e) => {
                                            e.preventDefault();
                                            insertMentionResolved(suggestion);
                                        }, selected: i === active, children: _jsx(Typography, { elementTheme: "body2", children: getSuggestionLabel(suggestion) }) }, getSuggestionKey(suggestion)))) }))] }), _jsxs(ComposerFooter, { children: [_jsx("div", { children: showValidationHint && hasMessageText && !isValidToSend && (_jsx(_Fragment, { children: mentionRequired ? (_jsx(Typography, { elementTheme: "body3", color: theme.colors.red_1, children: t('application|conversationInvalidMessageMentionHelp') })) : (_jsx(Typography, { elementTheme: "body3", color: theme.colors.red_1, children: t('application|conversationInvalidMessageEmptyHelp') })) })) }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [(!isCommentThread || hasMentionOptions) && (_jsx(MentionButton, { size: "small", trailingIcon: _jsx(AddNew, { size: 14, className: "mention-icon" }), onClick: () => {
                                                var _a, _b;
                                                setMentionSuggestionsOpen(true);
                                                setMentionQuery('');
                                                setActive(0);
                                                const el = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea;
                                                el === null || el === void 0 ? void 0 : el.focus();
                                            }, children: t('application|conversationMention') })), _jsx(SendButton, { size: "small", loading: isSending, onClick: () => void handleSend(), disabled: !isValidToSend || isSending, children: (_c = labels === null || labels === void 0 ? void 0 : labels.send) !== null && _c !== void 0 ? _c : t('application|send') })] })] })] }) }), messages.length === 0 && (labels === null || labels === void 0 ? void 0 : labels.empty), messages.length > 0 && (_jsxs(MessagesList, { children: [messages.map((m) => (_jsx(MessageItem, { message: m, onDelete: isCommentThread ? undefined : onDeleteMessage, onReply: isCommentThread ? undefined : handleReply, onJumpToMessage: handleJumpToMessage, canJumpToReplyParent: !!m.reply_parent_message_id &&
                            loadedMessageIds.has(m.reply_parent_message_id), containerRef: (node) => {
                            if (node) {
                                messageRefs.current.set(m.message_id, node);
                                return;
                            }
                            messageRefs.current.delete(m.message_id);
                        }, selfRole: selfRole, selfId: selfId }, m.message_id))), hasMore && (_jsx(LoadMoreWrapper, { children: _jsx(Button, { size: "small", onClick: onLoadMore, disabled: !!loadingMore, children: t('application|conversationLoadMoreLabel') }) }))] }))] }));
};
export default ConversationPanel;
